import { makeStyles } from '@map/theme';
import { Dialog, DialogActions, Typography } from '@mui/material';
import { useFormatMessage } from '@shared/hooks/intl';
import { uniqueId } from '@shared/util/react';
import Button from '@ui/Button';
import CloseIcon from '@ui/Icon/library/CloseIcon';
import { ReactNode, useState } from 'react';

const useStyles = makeStyles(({ borderRadius, spacing, palette, sizing }) => ({
  modal: {
    background: palette.common.white,
    padding: spacing(12, 7, 10),
    borderRadius: borderRadius('l'),
  },
  actions: {
    padding: 0,
    marginTop: spacing(4),
    justifyContent: 'center',
  },
  closeButton: {
    fontSize: sizing(7),
    position: 'absolute',
    top: spacing(2),
    right: spacing(2),
  },
}));

type Props = {
  open: boolean;
  showClose?: boolean;
  onClose: () => void;
  title?: ReactNode;
  text?: ReactNode;
  actionButtons?: ReactNode;
  className?: string;
};
const ModalDialog: FC<Props> = ({
  open,
  title,
  text,
  onClose,
  actionButtons,
  children,
  showClose = false,
  className,
}) => {
  const [dialogId] = useState(() => uniqueId('dialog-'));
  const { classes, cx } = useStyles();
  const t = useFormatMessage();

  return (
    <Dialog
      classes={{ paper: cx(classes.modal, className) }}
      open={open}
      onClose={onClose}
      aria-labelledby={title ? `${dialogId}-title` : undefined}
      aria-describedby={text ? `${dialogId}-description` : undefined}
    >
      {showClose && (
        <Button
          icon={true}
          variant="tertiary"
          size="medium"
          className={classes.closeButton}
          onClick={onClose}
          aria-label={t('common.close_text')}
        >
          <CloseIcon />
        </Button>
      )}
      {title && (
        <Typography variant="subtitle1" align="center" id={`${dialogId}-title`} gutterBottom>
          {title}
        </Typography>
      )}
      {text && (
        <Typography
          variant="body2"
          color="textSecondary"
          align="center"
          id={`${dialogId}-description`}
        >
          {text}
        </Typography>
      )}
      {children}
      <DialogActions className={classes.actions}>{actionButtons}</DialogActions>
    </Dialog>
  );
};

export default ModalDialog;
